import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '@mono/ui-components';
import { hasVisuals, SpecraVisualMarkdown } from '@mono/ui-components/chat-visuals';
import { Check, Copy } from 'lucide-react';
import { Children, isValidElement, memo, useCallback, useMemo, useState } from 'react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { MENTION_SCHEME, parseMentionHref } from '../lib/chat/mentions';
import { cn } from '../lib/utils';
import { copyToClipboard } from '../utils/downloadUtils';
import { MentionChip } from './ChatView/MentionChip';
import { CitationBadge } from './ChatView/message-parts/CitationBadge';
import { parseCitations, replaceCitationMarkers } from './ChatView/message-parts/parseCitations';
import { useChatVisualHost } from './ChatView/visualHost';
import { rehypeAllowlist } from './rehypeAllowlist';
import InViewOnce from './tools/atoms/InViewOnce';
import { LinkWithPreview } from './tools/atoms/LinkWithPreview';
import MermaidInline from './tools/atoms/MermaidInline';
const CodeBlock = ({ inline, className, children, ...props })=>{
    const [copied, setCopied] = useState(false);
    const match = /language-(\w+)/.exec(className || '');
    const language = match ? match[1] : '';
    const code = String(children).replace(/\n$/, '');
    const handleCopy = async ()=>{
        const success = await copyToClipboard(code);
        if (success) {
            setCopied(true);
            setTimeout(()=>setCopied(false), 2000);
        }
    };
    if (inline) {
        return /*#__PURE__*/ _jsx("code", {
            className: "relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold",
            ...props,
            children: children
        });
    }
    // Mermaid block (detect via language, className, or code content heuristics)
    const looksLikeMermaid = language === 'mermaid' || /(^|\s)mermaid(\s|$)/i.test(className || '') || /^\s*(graph|flowchart|sequenceDiagram|classDiagram|erDiagram|gantt|journey|pie)\b/.test(code);
    if (!inline && looksLikeMermaid) {
        return /*#__PURE__*/ _jsx(InViewOnce, {
            placeholder: /*#__PURE__*/ _jsx("div", {
                className: "h-24 w-full rounded-md bg-muted/40 animate-pulse"
            }),
            children: /*#__PURE__*/ _jsxs("div", {
                className: "relative group",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "absolute right-2 top-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity",
                        children: /*#__PURE__*/ _jsx(Button, {
                            size: "icon",
                            variant: "outline",
                            className: "h-8 w-8",
                            onClick: handleCopy,
                            "aria-label": "Copy diagram code",
                            children: copied ? /*#__PURE__*/ _jsx(Check, {
                                className: "h-4 w-4 text-green-500"
                            }) : /*#__PURE__*/ _jsx(Copy, {
                                className: "h-4 w-4"
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx(MermaidInline, {
                        code: code
                    })
                ]
            })
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "relative group",
        children: [
            /*#__PURE__*/ _jsx(Button, {
                size: "icon",
                variant: "ghost",
                className: "absolute right-2 top-2 z-10 h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity",
                onClick: handleCopy,
                "aria-label": "Copy code",
                children: copied ? /*#__PURE__*/ _jsx(Check, {
                    className: "h-4 w-4 text-green-500"
                }) : /*#__PURE__*/ _jsx(Copy, {
                    className: "h-4 w-4"
                })
            }),
            /*#__PURE__*/ _jsx(SyntaxHighlighter, {
                // @ts-expect-error - react-syntax-highlighter type definitions have a mismatch with the oneDark theme export
                style: oneDark,
                language: language,
                PreTag: "div",
                className: "rounded-lg !bg-zinc-950 !p-4",
                codeTagProps: {
                    style: {
                        fontSize: '0.875rem',
                        fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace'
                    }
                },
                ...props,
                children: code
            })
        ]
    });
};
/**
 * Structured citation data from backend.
 */ /** Links pass the default safety filter, except @-mentions, which are drawn as chips. */ const keepMentions = (url)=>url.startsWith(MENTION_SCHEME) ? url : defaultUrlTransform(url);
export const EnhancedMarkdown = /*#__PURE__*/ memo(({ content, className, enableCitations = true, structuredCitations, density = 'default' })=>{
    // Citations are read from the whole reply; their markers are replaced per
    // markdown run, after visual tags are split out (a placeholder inside a
    // tag's attribute would break it).
    const { citationMap, structuredCitationMap } = useMemo(()=>{
        if (!enableCitations || structuredCitations && structuredCitations.length > 0) {
            const structuredMap = new Map();
            for (const citation of structuredCitations ?? [])structuredMap.set(citation.index, citation);
            return {
                citationMap: new Map(),
                structuredCitationMap: structuredMap
            };
        }
        return {
            citationMap: parseCitations(content).citationMap,
            structuredCitationMap: new Map()
        };
    }, [
        content,
        enableCitations,
        structuredCitations
    ]);
    // Replace citation markers with HTML span placeholders that rehype-raw will parse.
    const withCitationMarkers = useCallback((text)=>{
        if (!enableCitations) return text;
        if (structuredCitationMap.size > 0) {
            return text.replace(/\[(\d+)\]/g, (match, id)=>structuredCitationMap.has(Number.parseInt(id, 10)) ? `<span data-structured-citation-id="${id}"></span>` : match);
        }
        return replaceCitationMarkers(text, (_, id)=>`<span data-citation-id="${id}"></span>`);
    }, [
        enableCitations,
        structuredCitationMap
    ]);
    // Memoize the components object to prevent recreation on every render
    const components = useMemo(()=>({
            // Neutralize default pre wrapper so custom renderers control styles
            pre: ({ children })=>{
                const child = Children.toArray(children)[0];
                if (/*#__PURE__*/ isValidElement(child)) {
                    return /*#__PURE__*/ _jsx(CodeBlock, {
                        className: child.props.className,
                        inline: false,
                        children: child.props.children
                    });
                }
                return /*#__PURE__*/ _jsx("div", {
                    children: children
                });
            },
            // Code blocks
            code: ({ className, children })=>/*#__PURE__*/ _jsx(CodeBlock, {
                    className: className,
                    inline: true,
                    children: children
                }),
            // Headings with better styling
            h1: ({ children })=>/*#__PURE__*/ _jsx("h1", {
                    className: density === 'chat' ? 'mb-2 mt-3 text-[13.5px] font-medium leading-[1.6] text-foreground first:mt-0' : 'mb-4 mt-6 text-2xl font-bold text-foreground first:mt-0',
                    children: children
                }),
            h2: ({ children })=>/*#__PURE__*/ _jsx("h2", {
                    className: density === 'chat' ? 'mb-2 mt-3 text-[13.5px] font-medium leading-[1.6] text-foreground first:mt-0' : 'mb-3 mt-6 text-xl font-semibold text-foreground first:mt-0',
                    children: children
                }),
            h3: ({ children })=>/*#__PURE__*/ _jsx("h3", {
                    className: density === 'chat' ? 'mb-1.5 mt-2 text-[13.5px] font-medium leading-[1.6] text-foreground first:mt-0' : 'mb-2 mt-4 text-lg font-semibold text-foreground first:mt-0',
                    children: children
                }),
            h4: ({ children })=>/*#__PURE__*/ _jsx("h4", {
                    className: density === 'chat' ? 'mb-1.5 mt-2 text-[13.5px] font-medium leading-[1.6] text-foreground first:mt-0' : 'mb-2 mt-3 text-base font-semibold text-foreground first:mt-0',
                    children: children
                }),
            // Citation placeholder spans (rendered from pre-processed HTML)
            // Supports both structured citations ([N] markers) and legacy @cite format
            span: ({ 'data-citation-id': citationId, 'data-structured-citation-id': structuredCitationId, children, ...props })=>{
                // Structured citation ([N] marker format)
                if (structuredCitationId) {
                    const id = Number.parseInt(structuredCitationId, 10);
                    const citation = structuredCitationMap.get(id);
                    if (citation) {
                        // Convert to ParsedCitation format for CitationBadge
                        const parsedCitation = {
                            id: citation.index,
                            content: citation.content,
                            documentId: citation.documentId,
                            documentName: citation.documentName
                        };
                        return /*#__PURE__*/ _jsx(CitationBadge, {
                            citation: parsedCitation
                        });
                    }
                    return null;
                }
                // Legacy citation (@cite:N:... format)
                if (citationId) {
                    const id = Number.parseInt(citationId, 10);
                    const citation = citationMap.get(id);
                    if (citation) {
                        return /*#__PURE__*/ _jsx(CitationBadge, {
                            citation: citation
                        });
                    }
                    return null;
                }
                return /*#__PURE__*/ _jsx("span", {
                    ...props,
                    children: children
                });
            },
            // Paragraphs
            p: ({ children })=>{
                const hasBlock = Children.toArray(children).some((child)=>{
                    if (!/*#__PURE__*/ isValidElement(child)) return false;
                    const t = child.type;
                    const isIntrinsic = typeof t === 'string';
                    const isCodeBlock = child.type === CodeBlock;
                    return isCodeBlock || isIntrinsic && (t === 'div' || t === 'pre' || t === 'table' || t === 'blockquote');
                });
                if (hasBlock) {
                    return /*#__PURE__*/ _jsx("div", {
                        className: "mb-4 last:mb-0 text-foreground",
                        children: children
                    });
                }
                return /*#__PURE__*/ _jsx("p", {
                    className: density === 'chat' ? 'mb-2 text-[13.5px] leading-[1.6] text-foreground last:mb-0' : 'mb-4 text-foreground leading-7 last:mb-0',
                    children: children
                });
            },
            // Lists
            ul: ({ children })=>/*#__PURE__*/ _jsx("ul", {
                    className: "list-disc list-outside ml-6 mb-4 space-y-1",
                    children: children
                }),
            ol: ({ children })=>/*#__PURE__*/ _jsx("ol", {
                    className: "list-decimal list-outside ml-6 mb-4 space-y-1",
                    children: children
                }),
            li: ({ children })=>/*#__PURE__*/ _jsx("li", {
                    className: density === 'chat' ? 'text-[13.5px] leading-[1.6] text-foreground' : 'text-foreground leading-7',
                    children: children
                }),
            // Blockquotes
            blockquote: ({ children })=>/*#__PURE__*/ _jsx("blockquote", {
                    className: "border-l-4 border-primary/50 pl-4 my-4 italic text-muted-foreground bg-muted/30 py-2 rounded-r",
                    children: children
                }),
            // Tables
            table: ({ children })=>/*#__PURE__*/ _jsx("div", {
                    className: "overflow-x-auto my-4",
                    children: /*#__PURE__*/ _jsx("table", {
                        className: "w-full border-collapse border border-border rounded-lg",
                        children: children
                    })
                }),
            thead: ({ children })=>/*#__PURE__*/ _jsx("thead", {
                    className: "bg-muted",
                    children: children
                }),
            tbody: ({ children })=>/*#__PURE__*/ _jsx("tbody", {
                    children: children
                }),
            tr: ({ children })=>/*#__PURE__*/ _jsx("tr", {
                    className: "border-b border-border hover:bg-muted/50 transition-colors",
                    children: children
                }),
            th: ({ children })=>/*#__PURE__*/ _jsx("th", {
                    className: "border border-border px-4 py-2 text-left font-semibold text-foreground",
                    children: children
                }),
            td: ({ children })=>/*#__PURE__*/ _jsx("td", {
                    className: "border border-border px-4 py-2 text-foreground",
                    children: children
                }),
            // Links
            a: ({ href, children })=>{
                const mention = parseMentionHref(href);
                if (mention) {
                    return /*#__PURE__*/ _jsx(MentionChip, {
                        id: mention.id,
                        kind: mention.kind,
                        children: children
                    });
                }
                return /*#__PURE__*/ _jsx(LinkWithPreview, {
                    href: href,
                    className: "text-primary hover:text-primary/80 underline underline-offset-4 transition-colors",
                    children: children
                });
            },
            // Strong and emphasis
            strong: ({ children })=>/*#__PURE__*/ _jsx("strong", {
                    className: "font-semibold text-foreground",
                    children: children
                }),
            em: ({ children })=>/*#__PURE__*/ _jsx("em", {
                    className: "italic text-foreground",
                    children: children
                }),
            // Horizontal rule
            hr: ()=>/*#__PURE__*/ _jsx("hr", {
                    className: "my-6 border-t border-border"
                })
        }), [
        citationMap,
        density,
        structuredCitationMap
    ]);
    const visualHost = useChatVisualHost();
    const renderMarkdown = useCallback((text)=>/*#__PURE__*/ _jsx(ReactMarkdown, {
            remarkPlugins: [
                remarkGfm
            ],
            rehypePlugins: [
                rehypeRaw,
                rehypeAllowlist
            ],
            components: components,
            urlTransform: keepMentions,
            children: withCitationMarkers(text)
        }), [
        components,
        withCitationMarkers
    ]);
    return /*#__PURE__*/ _jsx("div", {
        className: cn('prose prose-slate dark:prose-invert max-w-none prose-headings:font-display prose-ul:my-2 prose-li:my-0.5', density === 'chat' ? 'prose-p:text-[13.5px] prose-p:leading-[1.6]' : 'prose-p:text-[15px] prose-p:leading-7', className),
        children: hasVisuals(content) ? /*#__PURE__*/ _jsx(SpecraVisualMarkdown, {
            content: content,
            renderMarkdown: renderMarkdown,
            host: visualHost
        }) : renderMarkdown(content)
    });
});
